import { useCallback, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import useMerchantPortalAxios from "../../utils/axios";
import {
  setError,
  setFeatureData,
  setTableData,
  setTableLoading,
} from "../store/slices/appSlice";
import useUtilitiesApi from "./utilitiesApi";

function getSessionId() {
  return sessionStorage.getItem("SessionID");
}

function buildHistoryRequest(page, dateFrom, dateTo, utilityId) {
  return {
    SessionID: getSessionId(),
    page,
    pageSize: 10,
    DateFrom: dateFrom,
    DateTo: dateTo,
    UtilityID: utilityId,
  };
}

function getHistoryTotals(rows = []) {
  if (!rows.length) {
    return { totalNbOfPage: 0, totalCount: 0 };
  }

  return {
    totalNbOfPage: rows[0]?.TotalPages || 0,
    totalCount: rows[0]?.TotalCount || 0,
  };
}

export default function useNewBillsAPI() {
  const dispatch = useDispatch();
  const client = useMerchantPortalAxios();
  const { getAutomatedBills, setUtilityPaymentStatus } = useUtilitiesApi();
  const clientRef = useRef(client);
  const getAutomatedBillsRef = useRef(getAutomatedBills);
  const setUtilityPaymentStatusRef = useRef(setUtilityPaymentStatus);
  const [totalNbOfPage, setTotalNbOfPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [transactionBills, setTransactionBills] = useState([]);
  const [isAutomatable, setIsAutomatable] = useState(false);
  const [billsLoading, setBillsLoading] = useState(true);

  clientRef.current = client;
  getAutomatedBillsRef.current = getAutomatedBills;
  setUtilityPaymentStatusRef.current = setUtilityPaymentStatus;

  const GetAllBills = useCallback(async () => {
    const response = await clientRef.current.post("/v1/Bills/GetAllBills", {
      SessionID: getSessionId(),
    });

    const payload = response?.data?.Payload;
    dispatch(
      setFeatureData({
        section: "summary",
        key: "bills",
        value: {
          providers: payload,
        },
      }),
    );
    return payload;
  }, [dispatch]);

  const GetBillTransactionHistory = useCallback(
    async (pageNb, dateFrom, dateTo, utilityId) => {
      setBillsLoading(true);
      dispatch(setTableLoading({ key: "bills", loading: true }));
      dispatch(setError(null));

      try {
        const response = await clientRef.current.post(
          "/v1/Bills/GetBillTransactionHistory",
          buildHistoryRequest(pageNb, dateFrom, dateTo, utilityId),
        );

        const payload = response?.data?.Payload || {};
        const billsTransactions = payload.BillsTransactions || [];
        const totals = getHistoryTotals(billsTransactions);

        setTransactionBills(billsTransactions);
        setIsAutomatable(Boolean(payload.IsAutomatable));
        setTotalNbOfPage(totals.totalNbOfPage);
        setTotalCount(totals.totalCount);
        dispatch(
          setTableData({
            key: "bills",
            data: billsTransactions,
            pagination: {
              page: pageNb,
              totalPages: totals.totalNbOfPage,
              totalCount: totals.totalCount,
              pageSize: 10,
            },
          }),
        );
        dispatch(
          setFeatureData({
            section: "forms",
            key: "bills",
            value: {
              utilityId,
              dateFrom,
              dateTo,
            },
          }),
        );
        dispatch(
          setFeatureData({
            section: "summary",
            key: "bills",
            value: {
              isAutomatable: Boolean(payload.IsAutomatable),
              totalCount: totals.totalCount,
              totalPages: totals.totalNbOfPage,
            },
          }),
        );

        return payload;
      } catch (error) {
        dispatch(
          setTableData({
            key: "bills",
            data: [],
            pagination: {
              page: pageNb,
              totalPages: 0,
              totalCount: 0,
              pageSize: 10,
            },
          }),
        );
        dispatch(
          setError(
            error?.response?.data?.Message ||
              error?.response?.data?.message ||
              "Unable to load bills history.",
          ),
        );
        throw error;
      } finally {
        setBillsLoading(false);
        dispatch(setTableLoading({ key: "bills", loading: false }));
      }
    },
    [dispatch],
  );

  const UpdateAutomaticBillPayment = useCallback(async (payload) => {
    const response = await clientRef.current.post(
      "/v1/Bills/UpdateAutomaticBillPayment",
      payload,
    );

    return response?.data?.Payload;
  }, []);

  const GetAutomatedBills = useCallback(
    async (provider = "alfa") => {
      dispatch(
        setFeatureData({
          section: "summary",
          key: "bills",
          value: {
            automatedBillsLoading: true,
            automatedBillsError: "",
            automatedBillsProvider: provider,
          },
        }),
      );

      try {
        const response = await getAutomatedBillsRef.current(provider);
        if (response?.status === false || response?.IsSuccess === false) {
          throw new Error(
            response?.message ||
              response?.Message ||
              "Unable to load automated bills right now.",
          );
        }
        const automatedBills = Array.isArray(response?.data) ? response.data : [];

        dispatch(
          setFeatureData({
            section: "summary",
            key: "bills",
            value: {
              automatedBills,
              automatedBillsLoading: false,
              automatedBillsError: "",
              automatedBillsProvider: provider,
            },
          }),
        );

        return automatedBills;
      } catch (error) {
        const message =
          error?.response?.data?.PopupDescription ||
          error?.response?.data?.Message ||
          error?.message ||
          "Unable to load automated bills right now.";

        dispatch(
          setFeatureData({
            section: "summary",
            key: "bills",
            value: {
              automatedBills: [],
              automatedBillsLoading: false,
              automatedBillsError: message,
              automatedBillsProvider: provider,
            },
          }),
        );

        throw error;
      }
    },
    [dispatch],
  );

  const SetAutomatedBillPaymentStatus = useCallback(async (provider, payload) => {
    const response = await setUtilityPaymentStatusRef.current(provider, payload);
    if (response?.status === false || response?.IsSuccess === false) {
      throw new Error(
        response?.message ||
          response?.Message ||
          "Unable to update the automated payment right now.",
      );
    }
    return response;
  }, []);

  return {
    GetAllBills,
    GetAutomatedBills,
    GetBillTransactionHistory,
    SetAutomatedBillPaymentStatus,
    UpdateAutomaticBillPayment,
    TransactionBills: transactionBills,
    transactionBills,
    IsAutomatable: isAutomatable,
    isAutomatable,
    totalCount,
    totalNbOfPage,
    billsLoading,
  };
}
